import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate, NavLink } from 'react-router-dom';
import { Alert, Button, Dialog, Input, Loader } from '@dreamtree-org/twreact-ui';
import { ShieldCheck, Building2, LayoutDashboard, ScrollText, LogOut } from 'lucide-react';
import { platformGet, platformSend, setCsrfToken, errorOf } from './platformApi';

/**
 * Session + step-up plumbing for the platform console. `usePlatform().run(fn)` executes an
 * API call and, when the server answers STEP_UP_REQUIRED, asks for a fresh authenticator
 * code and retries once — so every sensitive screen gets step-up without its own dialog.
 */
const PlatformContext = createContext(null);

export function usePlatform() {
  return useContext(PlatformContext);
}

function StepUpDialog({ open, onSubmit, onCancel, error, busy }) {
  const [code, setCode] = useState('');
  useEffect(() => { if (open) setCode(''); }, [open]);
  return (
    <Dialog isOpen={open} onClose={onCancel} title="Confirm it's you" size="sm">
      <form
        className="space-y-3"
        onSubmit={(e) => { e.preventDefault(); onSubmit(code); }}
      >
        <p className="text-sm text-secondary-600">This action changes credentials or a company&apos;s lifecycle. Enter a current code from your authenticator app (or a recovery code).</p>
        {error && <Alert variant="error">{error}</Alert>}
        <Input label="Authenticator code" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" inputMode="numeric" autoFocus />
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={busy || !code.trim()}>{busy ? 'Checking…' : 'Confirm'}</Button>
        </div>
      </form>
    </Dialog>
  );
}

export function RequirePlatformSession() {
  const loc = useLocation();
  const nav = useNavigate();
  const [me, setMe] = useState(undefined);
  const [stepUp, setStepUp] = useState(null);
  const [stepUpError, setStepUpError] = useState('');
  const [stepUpBusy, setStepUpBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const data = await platformGet('/me');
      setCsrfToken(data.csrfToken);
      setMe(data);
    } catch {
      setMe(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const run = useCallback(async (fn) => {
    try {
      return await fn();
    } catch (ex) {
      if (errorOf(ex).code !== 'STEP_UP_REQUIRED') throw ex;
      await new Promise((resolve, reject) => setStepUp({ resolve, reject }));
      return fn();
    }
  }, []);

  const submitStepUp = async (code) => {
    setStepUpBusy(true);
    setStepUpError('');
    try {
      await platformSend('post', '/auth/step-up', { code });
      stepUp.resolve();
      setStepUp(null);
    } catch (ex) {
      setStepUpError(errorOf(ex).message);
    } finally {
      setStepUpBusy(false);
    }
  };

  const cancelStepUp = () => {
    if (stepUp) stepUp.reject(Object.assign(new Error('Cancelled'), { cancelled: true }));
    setStepUp(null);
    setStepUpError('');
  };

  const logout = useCallback(async () => {
    try { await platformSend('post', '/auth/logout'); } catch { /* the cookie is cleared either way below */ }
    setCsrfToken(null);
    nav('/platform/login', { replace: true });
  }, [nav]);

  const value = useMemo(() => ({ me, refresh, run, logout }), [me, refresh, run, logout]);

  if (me === undefined) return <div className="p-8 flex justify-center"><Loader /></div>;
  if (me === null) return <Navigate to="/platform/login" replace state={{ from: loc.pathname }} />;
  if (me.authStrength !== 'totp' && loc.pathname !== '/platform/setup-2fa') return <Navigate to="/platform/setup-2fa" replace />;

  return (
    <PlatformContext.Provider value={value}>
      <Outlet />
      <StepUpDialog open={!!stepUp} onSubmit={submitStepUp} onCancel={cancelStepUp} error={stepUpError} busy={stepUpBusy} />
    </PlatformContext.Provider>
  );
}

const NAV = [
  { to: '/platform', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/platform/companies', label: 'Companies', icon: Building2 },
  { to: '/platform/audit', label: 'Audit', icon: ScrollText },
];

/** Console chrome: a distinct header so nobody mistakes the platform realm for a tenant app. */
export function PlatformLayout() {
  const { me, logout } = usePlatform();
  return (
    <div className="min-h-screen flex flex-col bg-secondary-50 dark:bg-secondary-950">
      <header className="flex flex-wrap items-center gap-3 px-4 py-2 border-b border-secondary-200 dark:border-secondary-800 bg-white dark:bg-secondary-900">
        <div className="flex items-center gap-2 font-semibold">
          <ShieldCheck size={18} className="text-amber-600" aria-hidden="true" /> Platform console
        </div>
        <nav className="flex gap-1" aria-label="Platform">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => `flex items-center gap-1 px-2 py-1 rounded text-sm ${isActive ? 'bg-secondary-100 dark:bg-secondary-800 font-medium' : 'text-secondary-600 hover:bg-secondary-100 dark:hover:bg-secondary-800'}`}
            >
              <Icon size={14} aria-hidden="true" /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-2 text-sm text-secondary-600">
          <span className="hidden sm:inline">{me.email}</span>
          <Button size="sm" variant="secondary" onClick={logout}><LogOut size={14} className="mr-1 inline" /> Sign out</Button>
        </div>
      </header>
      <main className="flex-1 min-h-0 flex flex-col"><Outlet /></main>
    </div>
  );
}
